import { COLOR_CHART_STATUSES } from 'constants/analytics';
import { colors } from 'constants/styles/colors';
import { CHART_TITLE_MAP } from 'utils/chartCsvExport';
import { PngExportSeries } from 'utils/chartPngExport';

export type ExportChartView =
  | 'users'
  | 'activities'
  | 'videos'
  | 'videos_overall'
  | 'quizzes'
  | 'labs';

export interface ExportSeriesSpec {
  key: string;
  label: string;
  /** Included only when the account has Skill Dive enabled. */
  skillDiveOnly?: boolean;
}

export interface ExportChartSpec {
  view: ExportChartView;
  title: string;
  series: ExportSeriesSpec[];
}

// The six page-export charts. Every series a view can show is listed (including
// ones the dashboard defaults to off) so the export always renders maximum data.
// Video is two charts: started/finished counts and time watched.
export const EXPORT_CHART_SPECS: ExportChartSpec[] = [
  {
    view: 'users',
    title: CHART_TITLE_MAP.users,
    series: [{ key: 'licensed', label: 'Licensed users with activity' }],
  },
  {
    view: 'activities',
    title: CHART_TITLE_MAP.activities,
    series: [
      { key: 'overall', label: 'All activities' },
      { key: 'labs', label: 'Labs' },
      { key: 'videos', label: 'Videos' },
      { key: 'quizzes', label: 'Quizzes' },
    ],
  },
  {
    view: 'videos',
    title: CHART_TITLE_MAP.videos,
    series: [
      { key: 'started', label: 'Started videos' },
      { key: 'finished', label: 'Finished videos' },
    ],
  },
  {
    view: 'videos_overall',
    title: CHART_TITLE_MAP.videos_overall,
    series: [{ key: 'watched', label: 'Time watched' }],
  },
  {
    view: 'quizzes',
    title: CHART_TITLE_MAP.quizzes,
    series: [
      { key: 'started', label: 'Started quizzes' },
      { key: 'finished', label: 'Finished quizzes' },
      { key: 'passed', label: 'Passed quizzes' },
    ],
  },
  {
    view: 'labs',
    title: CHART_TITLE_MAP.labs,
    series: [
      { key: 'total', label: 'Total labs started' },
      { key: 'standard', label: 'Standard labs started' },
      { key: 'skill_dive', label: 'Skill Dive labs started', skillDiveOnly: true },
    ],
  },
];

export function getSeriesColor(key: string): string {
  const c = COLOR_CHART_STATUSES[key.toUpperCase()];
  return c?.stroke ?? c?.fill ?? colors.fonts.lightGray;
}

export function getExportSeries(spec: ExportChartSpec, hasSkillDive: boolean): ExportSeriesSpec[] {
  return spec.series.filter((s) => !s.skillDiveOnly || hasSkillDive);
}

/** Legend metadata (label + color per series) passed to captureChartAsPng. */
export function getExportChartLegend(
  spec: ExportChartSpec,
  hasSkillDive: boolean
): PngExportSeries[] {
  return getExportSeries(spec, hasSkillDive).map((s) => ({
    label: s.label,
    color: getSeriesColor(s.key),
  }));
}

/** All-series-enabled dataDisplay for ActiveUsersChart. */
export function getExportDataDisplay(
  spec: ExportChartSpec,
  hasSkillDive: boolean
): { key: string; checked: boolean }[] {
  return getExportSeries(spec, hasSkillDive).map((s) => ({ key: s.key, checked: true }));
}
